/**
 * Utility to load template HTML content
 * Templates are now stored in the database, but this utility provides
 * fallback loading from public folder for backward compatibility
 */

/**
 * Loads a template by its path from public folder (fallback only)
 * Templates should be loaded from database via template.content
 * @param templatePath - The path to the template (public folder path)
 * @returns The template content as a string, or null if not found
 */
export async function loadTemplateContent(templatePath: string): Promise<string | null> {
  // Only try to fetch from public folder if path starts with /
  const normalizedPath = templatePath.trim();

  if (normalizedPath.startsWith('/')) {
    try {
      const response = await fetch(normalizedPath);
      if (response.ok) {
        return await response.text();
      }
    } catch (error) {
      console.error('Error fetching template from public folder:', error);
    }
  }

  return null;
}

/**
 * Get template content synchronously (deprecated - templates are in database)
 * @param templatePath - The path to the template
 * @returns Always returns null - templates should be loaded from database
 */
export function getTemplateContent(_templatePath: string): string | null {
  // Templates are now in database, this is for backward compatibility only
  return null;
}

/**
 * Processes HTML template with dynamic field placeholders
 * Matches the logic used in PDFService.processTemplate for consistency
 * @param html - The HTML template content
 * @param dynamicFields - Object with field keys and values to replace
 * @param title - Optional document title to replace {{title}} placeholder
 * @param clientName - Optional client name to replace {{client_name}} placeholder
 * @param clientEmail - Optional client email to replace {{client_email}} placeholder
 * @returns Processed HTML with placeholders replaced
 */
export function processTemplate(
  html: string,
  dynamicFields: Record<string, any>,
  title?: string,
  clientName?: string,
  clientEmail?: string
): string {
  let processed = html;

  // Replace simple placeholders: {{fieldName}}
  Object.entries(dynamicFields || {}).forEach(([key, value]) => {
    const placeholder = new RegExp(`\\{\\{${key}\\}\\}`, 'g');
    const displayValue = value != null ? String(value) : '';
    processed = processed.replace(placeholder, displayValue);
  });

  // Replace title placeholder if provided
  if (title) {
    processed = processed.replace(/\{\{title\}\}/g, title);
  }

  // Replace client name placeholder if provided
  if (clientName) {
    processed = processed.replace(/\{\{client_name\}\}/g, clientName);
  }

  // Replace client email placeholder if provided
  if (clientEmail) {
    processed = processed.replace(/\{\{client_email\}\}/g, clientEmail);
  }

  // Handle conditional blocks: {{#if fieldName}}...{{/if}}
  const conditionalRegex = /\{\{#if\s+(\w+)\}\}([\s\S]*?)\{\{\/if\}\}/g;
  processed = processed.replace(conditionalRegex, (_match, fieldName, content) => {
    const value = dynamicFields[fieldName];
    if (value && value !== false && value !== 'false' && value !== 0 && value !== '') {
      return content;
    }
    return '';
  });

  return processed;
}
